import React, { useState } from "react";
import { Link, Navigate, useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Mail, Lock, Loader2, ArrowRight, ShieldCheck, Zap, TrendingUp, Sparkles, Youtube, Server, Radio } from "lucide-react";
import { GoogleSignInButton } from "@/components/auth/GoogleSignInButton";
import { FacebookSignInButton } from "@/components/auth/FacebookSignInButton";
import { LinkedInSignInButton } from "@/components/auth/LinkedInSignInButton";
import { PasskeySignInButton } from "@/components/auth/PasskeySignInButton";
import { AuthBrandPanel } from "@/components/auth/AuthBrandPanel";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/contexts/AuthContext";
import { SEO } from "@/components/shared/SEO";
import { BrandMark } from "@/components/shared/BrandMark";
import { COMPANY } from "@/lib/company";
import { api } from "@/lib/api";

// Deciding where a just-logged-in user should land. New signups have
// persona=null AND profile_complete=false → send them through /onboarding.
// Legacy users (persona=null but profile_complete=true) are grandfathered.
function landingRouteFor(u: any): string {
  if (u && !u.persona && !u.profile_complete) return "/onboarding";
  return "/dashboard";
}

export const Login: React.FC = () => {
  const { user, login, loading, loginWithToken } = useAuth();
  const nav = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [demoLoading, setDemoLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [demoEnabled, setDemoEnabled] = useState<boolean>(true);

  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get<{ enabled: boolean }>("/auth/demo-login/status");
        if (!cancelled) setDemoEnabled(!!data?.enabled);
      } catch {
        if (!cancelled) setDemoEnabled(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (!loading && user) {
    // Where should an already-authenticated user land? If they still owe
    // us their persona, funnel them through /onboarding first — same rule
    // used by the imperative onSubmit handler (landingRouteFor). Deep-link
    // via `location.state.from` is honoured once onboarding is done.
    const persistedFrom = (location.state as any)?.from;
    const to = landingRouteFor(user) === "/onboarding"
      ? "/onboarding"
      : (persistedFrom || "/dashboard");
    return <Navigate to={to} replace />;
  }

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const u = await login(email, password);
      toast.success("Welcome back");
      nav(landingRouteFor(u));
    } catch (err: any) {
      toast.error(err?.response?.data?.detail || "Login failed");
    } finally {
      setSubmitting(false);
    }
  };

  const onGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  // Judge/Demo login — hits POST /api/auth/demo-login which hands out a
  // token for the pre-seeded showcase workspace. Zero credentials
  // required. Powers the "Continue as Judge" button that reviewers use
  // for contest submissions.
  const onDemoLogin = async () => {
    setDemoLoading(true);
    try {
      const { data } = await api.post("/auth/demo-login");
      await loginWithToken(data.token, data.user);
      toast.success("Welcome — you're inside the demo workspace 🎁");
      nav(landingRouteFor(data.user));
    } catch (err: any) {
      toast.error(err?.response?.data?.detail || "Demo login unavailable — please try again in a moment.");
    } finally {
      setDemoLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-5 bg-[color:var(--sg-bg)] overflow-x-hidden">
      <SEO
        title="Log in"
        description="Log in to SociBaba — the AI social intelligence workspace for creators, brands and businesses."
        keywords={["SociBaba login","social media dashboard login","creator dashboard","brand social CRM login"]}
        path="/login"
      />

      {/* Left hero pane — refreshed SociBaba brand: SibaScore, live ticker, trust markers. */}
      <div className="lg:col-span-2">
        <AuthBrandPanel variant="login" />
      </div>

      {/* Right form pane */}
      <div className="lg:col-span-3 flex flex-col lg:flex-row items-stretch lg:items-center justify-start lg:justify-center p-6 lg:p-16 relative">
        <div className="w-full max-w-md mx-auto">
          <div className="sg-label mb-3 text-center">Log in</div>
          <h1 className="font-display text-3xl lg:text-4xl font-bold tracking-tight mb-2 text-center">
            Welcome back
          </h1>
          <p className="text-sm text-[color:var(--sg-muted)] mb-8 text-center">
            Sign in to your workspace to continue.
          </p>

          <form onSubmit={onSubmit} className="space-y-5" data-testid="login-form">
            {/* Judge / Demo login — first thing contest reviewers see.
                One click hands out a token for the pre-seeded showcase
                workspace so they can explore the full app without
                registering. Distinct visual treatment (fuchsia gradient
                + sparkle) so it stands out from the regular auth flow.
                Hidden when the super-admin has flipped demo-login OFF. */}
            {demoEnabled && (<>
            <button
              type="button"
              onClick={onDemoLogin}
              disabled={demoLoading}
              data-testid="login-demo-judge"
              className="group relative w-full h-12 rounded-lg overflow-hidden font-semibold text-white shadow-lg shadow-fuchsia-500/25 hover:shadow-fuchsia-500/40 transition-all disabled:opacity-70 disabled:cursor-not-allowed"
            >
              <span className="absolute inset-0 bg-gradient-to-r from-orange-500 via-fuchsia-500 to-cyan-500 bg-[length:200%_100%] animate-[gradient_6s_ease_infinite] group-hover:brightness-110" />
              <span className="relative flex items-center justify-center gap-2">
                {demoLoading ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Sparkles className="h-4 w-4" />
                )}
                {demoLoading ? "Loading demo workspace…" : "Continue as Demo"}
                <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
              </span>
            </button>
            {/* Judge-mode trust badges — reassure reviewers that this is a
                shipping product, not a mock demo. Backend endpoints, YouTube
                channel, and Twilio SMS/WhatsApp are wired end-to-end. */}
            <div
              data-testid="login-judge-trust-row"
              className="grid grid-cols-3 gap-2 -mt-1"
            >
              <div className="rounded-lg border border-emerald-200 bg-emerald-50/60 px-2 py-1.5 flex items-center gap-1.5">
                <Server className="h-3 w-3 text-emerald-600 shrink-0" />
                <div className="text-[10px] leading-tight">
                  <div className="font-semibold text-emerald-800">Backend live</div>
                  <div className="text-emerald-600/80">FastAPI · Mongo</div>
                </div>
              </div>
              <a
                href="https://www.youtube.com/@socibaba"
                target="_blank"
                rel="noreferrer noopener"
                data-testid="login-judge-youtube-badge"
                className="rounded-lg border border-rose-200 bg-rose-50/60 px-2 py-1.5 flex items-center gap-1.5 hover:border-rose-400 transition-colors"
              >
                <Youtube className="h-3 w-3 text-rose-600 shrink-0" />
                <div className="text-[10px] leading-tight">
                  <div className="font-semibold text-rose-800">YouTube live</div>
                  <div className="text-rose-600/80">@socibaba</div>
                </div>
              </a>
              <div className="rounded-lg border border-sky-200 bg-sky-50/60 px-2 py-1.5 flex items-center gap-1.5">
                <Radio className="h-3 w-3 text-sky-600 shrink-0" />
                <div className="text-[10px] leading-tight">
                  <div className="font-semibold text-sky-800">Twilio wired</div>
                  <div className="text-sky-600/80">SMS · WhatsApp</div>
                </div>
              </div>
            </div>
            <p
              data-testid="login-judge-real-note"
              className="text-[11px] text-center text-[color:var(--sg-muted)] leading-relaxed"
            >
              <span className="font-semibold text-emerald-700 dark:text-emerald-400">Not a mock demo.</span>{" "}
              Fully backend-integrated platform · 80 real products, live leads,
              real orders, real Stripe test-mode checkout · ready to ship today.
            </p>
            </>)}

            <div className="flex flex-col items-center gap-2 py-1">
              <div className="w-full max-w-[320px] mx-auto space-y-2">
                <GoogleSignInButton mode="signin" />
                <FacebookSignInButton mode="signin" />
                <LinkedInSignInButton mode="signin" />
                <PasskeySignInButton mode="signin" email={email} />
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="flex-1 h-px bg-[color:var(--sg-border)]" />
              <span className="text-[10px] uppercase tracking-wider text-[color:var(--sg-muted)]">or with email</span>
              <div className="flex-1 h-px bg-[color:var(--sg-border)]" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="email" className="text-xs font-semibold uppercase tracking-wider text-[color:var(--sg-muted)]">
                Email
              </Label>
              <div className="relative group">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[color:var(--sg-muted)] group-focus-within:text-[color:var(--sg-primary)] transition-colors" />
                <Input
                  id="email"
                  data-testid="login-email"
                  type="email"
                  placeholder="you@company.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="pl-9 h-11 bg-white"
                  required
                  autoComplete="email"
                />
              </div>
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="password" className="text-xs font-semibold uppercase tracking-wider text-[color:var(--sg-muted)]">
                  Password
                </Label>
                <Link
                  to="/forgot-password"
                  data-testid="login-forgot-link"
                  className="text-xs text-[color:var(--sg-primary)] hover:underline font-medium"
                >
                  Forgot password?
                </Link>
              </div>
              <div className="relative group">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[color:var(--sg-muted)] group-focus-within:text-[color:var(--sg-primary)] transition-colors" />
                <Input
                  id="password"
                  data-testid="login-password"
                  type={showPassword ? "text" : "password"}
                  placeholder="Enter your password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="pl-9 pr-14 h-11 bg-white"
                  required
                  autoComplete="current-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((s) => !s)}
                  data-testid="login-toggle-password"
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[11px] font-semibold uppercase tracking-wider text-[color:var(--sg-muted)] hover:text-[color:var(--sg-fg)]"
                >
                  {showPassword ? "Hide" : "Show"}
                </button>
              </div>
            </div>

            <Button
              type="submit"
              data-testid="login-submit"
              disabled={submitting}
              className="w-full h-11 bg-[color:var(--sg-fg)] text-white hover:bg-black hover:text-white font-semibold group"
            >
              {submitting ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <>
                  Log in
                  <ArrowRight className="h-4 w-4 ml-1.5 group-hover:translate-x-0.5 transition-transform" />
                </>
              )}
            </Button>
          </form>

          <p className="text-sm text-[color:var(--sg-muted)] mt-6 text-center">
            No account?{" "}
            <Link to="/signup" data-testid="link-signup" className="text-[color:var(--sg-primary)] font-semibold hover:underline">
              Create one
            </Link>
          </p>

          <div className="mt-8 pt-6 border-t border-[color:var(--sg-border)] flex flex-wrap justify-center gap-x-4 gap-y-1 text-xs text-[color:var(--sg-muted)]">
            <Link to="/privacy" className="hover:text-[color:var(--sg-fg)]" data-testid="login-privacy-link">Privacy</Link>
            <Link to="/terms" className="hover:text-[color:var(--sg-fg)]" data-testid="login-terms-link">Terms</Link>
            <Link to="/cookies" className="hover:text-[color:var(--sg-fg)]">Cookies</Link>
            <Link to="/data-deletion" className="hover:text-[color:var(--sg-fg)]">Data deletion</Link>
            <Link to="/contact" className="hover:text-[color:var(--sg-fg)]">Contact</Link>
          </div>
        </div>
      </div>
    </div>
  );
};
